import React from "react"
import { AbsoluteFill, Sequence, spring, useCurrentFrame, useVideoConfig } from "remotion"
import { msToFrames } from "../../utils/timing"
import type { Track } from "../../schemas"

interface EmojiTrackProps {
  track: Track
}

export const EmojiTrack: React.FC<EmojiTrackProps> = ({ track }) => {
  const { fps } = useVideoConfig()

  const segments = track.sections.flatMap(s => s.segments)

  return (
    <AbsoluteFill>
      {segments.map((segment) => {
        const inFrame = msToFrames(segment.timeline.in, fps)
        const duration = msToFrames(segment.timeline.out - segment.timeline.in, fps)
        const content = segment.content
        if (!content?.emoji) return null

        const position = segment.position ?? { xPct: 0, yPct: 0, wPct: 0.2, hPct: 0.2 }

        return (
          <Sequence key={segment.id} from={inFrame} durationInFrames={Math.max(duration, 1)}>
            <EmojiSegmentView
              emoji={content.emoji}
              scale={content.scale ?? 1.0}
              position={position}
              fps={fps}
            />
          </Sequence>
        )
      })}
    </AbsoluteFill>
  )
}

const EmojiSegmentView: React.FC<{
  emoji: string
  scale: number
  position: { xPct: number; yPct: number }
  fps: number
}> = ({ emoji, scale, position, fps }) => {
  const frame = useCurrentFrame()

  // Pop-in animation using spring
  const springValue = spring({
    frame,
    fps,
    config: { damping: 12, stiffness: 200 },
  })

  const left = `${50 + (position.xPct ?? 0) * 50}%`
  const top = `${50 + (position.yPct ?? 0) * 50}%`

  return (
    <AbsoluteFill>
      <div
        style={{
          position: "absolute",
          left,
          top,
          transform: `translate(-50%, -50%) scale(${springValue * scale})`,
          fontSize: 64,
          lineHeight: 1,
        }}
      >
        {emoji}
      </div>
    </AbsoluteFill>
  )
}
